import TableOnly from "components/DataTable/TableOnly";
import IssueReportPdfButton from "components/Pdfs/IssueReport/IssueReportPdfButton";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import * as XLSX from "xlsx";
import PaginationWithState from "components/Pagination/PaginationWithState";

export default function MaterialIssueHistory() {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear() - 1, now.getMonth() - 1, 2)
      .toISOString()
      .slice(0, 10)
      .replace("T", " ");
  });

  const [endDate, setEndDate] = useState(
    new Date().toISOString().slice(0, 10).replace("T", " ")
  );

  const [sortBy, setSortBy] = useState("none");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterBy, setFilterBy] = useState("all");

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [issueReport, setIssueReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Functions
   ********************************************* */
  // Fetch all data for export
  const fetchAllDataForExport = async () => {
    try {
      const endDateObj = new Date(endDate);
      const endDateString = endDateObj.toISOString().slice(0, 10);

      const { data } = await API.post(
        "/manufacturer-issues-report-with-pagination?page=1&limit=9999",
        {
          startDate: startDate,
          endDate: endDateString,
          sortBy: sortBy,
          sortDirection: sortDirection,
          query: searchTerm,
          filterBy: filterBy,
        },
        { headers: authHeader() }
      );

      if (data?.success) {
        return data?.data || [];
      }
      return [];
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
      return [];
    }
  };

  //get Issue Report
  const getIssueReport = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);

        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;

        // get only date from endDate
        const endDateObj = new Date(endDate);
        const endDateString = endDateObj.toISOString().slice(0, 10);

        const { data } = await API.post(
          "/manufacturer-issues-report-with-pagination?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            startDate: startDate,
            endDate: endDateString,
            sortBy: sortBy,
            sortDirection: sortDirection,
            query: usedQuery,
            filterBy: filterBy,
          },
          { headers: authHeader() }
        );
        if (data?.success) {
          setIssueReport(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        }
      } catch (err) {
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    },
    [
      pageNumber,
      pageLimit,
      sortBy,
      sortDirection,
      startDate,
      endDate,
      filterBy,
      dispatch,
    ]
  );

  // Getting 12 Hours Time from SQL timestamp
  const convertSqlTimestampToDate = (sqlTime) => {
    const timestamp = sqlTime;
    const date = new Date(timestamp);
    const time = date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return time;
  };

  const getIssueQty = (item) => {
    let issueQty = item?.m_issues
      ?.reduce((acc, item) => acc + item.qty, 0)
      ?.toFixed(3);

    if (issueQty === "0.000") {
      return "0";
    }
    return issueQty || 0;
  };

  const downloadExcel = async () => {
    try {
      setExportLoading(true);
      const dataToExport = await fetchAllDataForExport();

      if (dataToExport.length === 0) {
        dispatch(
          setNotification({
            message: "No data found to export",
            type: "info",
          })
        );
        return;
      }

      const excelData = dataToExport.map((item) => ({
        "Issue Date": item?.issueDate
          ? convertSqlTimestampToDate(item?.issueDate)
          : "-",
        "T-ID": item?.tid || "-",
        "Lot #": item?.lotId || "-",
        Material: item?.m_materialList?.styleId || "-",
        Color: item?.m_materialList?.color || "-",
        Qty: item?.qty || 0,
        "Order ID": item?.orderId || "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Issue History");
      XLSX.writeFile(
        workbook,
        `issue-history-${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      dispatch(
        setNotification({
          message: "Export successful",
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setExportLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      getIssueReport({ page: pageNumber, query: searchTerm });
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit]);

  /*   Table functions
   ********************************************* */
  const btn1 = () => (
    <div className="d-flex justify-content-end mb-0">
      <Dropdown>
        <Dropdown.Toggle
          className="px-x ms-1"
          variant="primary"
          id="dropdown-basic"
          disabled={exportLoading}
        >
          {exportLoading ? "Exporting..." : "Reports"}
        </Dropdown.Toggle>

        <Dropdown.Menu className="custom-shadow-sm">
          <Dropdown.Item
            onClick={(e) => {
              e.stopPropagation();
            }}
            as="div"
            className="px-2 m-0 "
          >
            <IssueReportPdfButton
              startDate={startDate}
              endDate={endDate}
              sortBy={sortBy}
              sortDirection={sortDirection}
              query={searchTerm}
              filterBy={filterBy}
              btnTitle={"Export PDF"}
              btnClasses={"btn w-100"}
              btnColor={"text-start btn"}
            />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={(e) => {
              e.stopPropagation();
            }}
            as="div"
            className="px-2 m-0"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadExcel();
              }}
              disabled={exportLoading}
              className="btn w-100 text-start btn-success"
            >
              {exportLoading ? "Exporting..." : "Export Excel"}
            </button>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      <div className="d-flex align-items-center">
        <div className="me-2 text-nowrap">Sort By:</div>{" "}
        <select
          className="form-select form-select-sm"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="none">None</option>
          <option value="issueDate">Date</option>
          <option value="tid">T-ID</option>
          <option value="lotId">Lot #</option>
          <option value="m_materialList.styleId">Material</option>
          <option value="m_materialList.color">Color</option>
          <option value="qty">Qty</option>
          <option value="orderId">Order ID</option>
        </select>
      </div>{" "}
      <div className="d-flex align-items-center ms-2">
        <div className="me-2 text-nowrap">Direction:</div>
        <select
          className="form-select form-select-sm"
          value={sortDirection}
          onChange={(e) => setSortDirection(e.target.value)}
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>
      {/* From */}
      <div className="row ">
        <div className="col-3  d-flex justify-content-start ms-1 align-items-center ">
          <div className="">From</div>
        </div>
        <div className="col-8 px-0">
          <input
            className="form-control"
            value={startDate || ""}
            onChange={(e) => setStartDate(e.target.value)}
            type={"date"}
          />
        </div>
      </div>
      {/* To */}
      <div className="row">
        <div className="col-3  d-flex justify-content-start ms-1 align-items-center ">
          <div className="">TO</div>
        </div>
        <div className="col-8 px-0">
          <input
            className="form-control"
            value={endDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            type={"date"}
          />
        </div>
      </div>
      <div className="d-flex justify-content-end ">
        <button
          onClick={() => {
            getIssueReport({ page: 1, query: searchTerm });
            setShowDateFilter(!showDateFilter);
          }}
          className="btn btn-success btn-sm mx-2"
        >
          FILTER
        </button>
      </div>
    </div>
  );

  const table_head = [
    { name: "Issue date", value: "issueDate" },
    { name: "T-ID", value: "tid" },
    { name: "Lot #", value: "lotId" },
    { name: "Material", value: "m_materialList.styleId" },
    { name: "Color", value: "m_materialList.color" },
    { name: "Qty", value: "qty" },
    { name: "Order ID", value: "orderId" },
    { name: "Actions", value: "" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">
        {" "}
        {item?.issueDate ? convertSqlTimestampToDate(item?.issueDate) : "-"}
      </td>
      <td className="align-middle py-1">{item?.tid}</td>
      <td className="align-middle py-1">{item?.lotId}</td>
      <td className="align-middle py-1">{item?.m_materialList?.styleId}</td>
      <td className="align-middle py-1">{item?.m_materialList?.color}</td>
      <td className="align-middle py-1">{item?.qty}</td>
      <td className="align-middle py-1">{item?.orderId}</td>
      <td className="align-middle py-1 text-end">
        <Link
          to={
            "/manufacturer/material-stock?tab=material-issue&materialId=" +
            item?.materialId +
            "&page=1&limit=10&lotId=" +
            item?.lotId +
            "&tid=" +
            item?.tid
          }
          className="btn btn-sm btn-success ms-1"
        >
          View
        </Link>
      </td>
    </tr>
  );

  return (
    <div className="mt-3 ps-2 pe-3">
      {loading && <Spinner />}

      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div>
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex align-items-center">
          {buttons()}

          <div>
            <select
              value={filterBy}
              className="form-select me-1 border-info"
              onChange={(e) => setFilterBy(e.target.value)}
              name="filterBy"
              id="filterBy"
            >
              <option value="all">All</option>
              <option value="issueDate">Date</option>
              <option value="tid">T-ID</option>
              <option value="lotId">Lot #</option>
              <option value="m_materialList.styleId">Material</option>
              <option value="m_materialList.color">Color</option>
              <option value="qty">Qty</option>
              <option value="orderId">Order ID</option>
            </select>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getIssueReport({ query: searchTerm, page: 1 });
              } else {
                getIssueReport({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getIssueReport({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {btn1()}
        </div>
      </div>

      <div className="horizontal-scrollable d-flex flex-grow-1">
        <TableOnly
          table_head={table_head}
          table_body={table_body}
          table_data={issueReport}
          disableSorting={true}
          actionsOnEnd={true}
        />
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>
    </div>
  );
}
